import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallContentBox } from '../../ToolCallContentBox';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { type ParsedSuccess } from '../../tool-call-utils';
import { ToolCallError } from '../../ToolCallError';

interface SoslSearchTool {
  name: AgentTools.sf_sosl_search;
  args: { search: string };
}

export type SoslSearchToolCall = ToolCall<SoslSearchTool['name'], SoslSearchTool['args']>;

export type SoslSearchToolCallSummary = ToolCallSummary<
  SoslSearchTool['name'],
  SoslSearchTool['args']
>;

interface SoslResult {
  records: Record<string, unknown>[];
  done: boolean;
  totalSize?: number;
}

export function SoslToolCall({ toolCall }: { toolCall: SoslSearchToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<SoslResult>(
    toolCall,
    ({ toolCallState, parsedResult }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_sosl_search.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_sosl_search.title.errors');
        case ToolCallState.Success: {
          const successParsedResult = parsedResult as ParsedSuccess<SoslResult>;
          const totalSize = successParsedResult.results?.totalSize;
          const recordsLength = Array.isArray((successParsedResult.results as any)?.records)
            ? ((successParsedResult.results as any).records as unknown[]).length
            : 0;
          return t('toolCalls.sf_sosl_search.title.success', {
            count: totalSize ?? recordsLength,
          });
        }
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.sf_sosl_search.searchResults'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallContentBox content={toolCall.results} />
          )
        ) : null,
    },
    {
      id: 'query',
      label: t('toolCalls.sf_sosl_search.searchQuery'),
      content: <ToolCallContentBox content={toolCall.args.search} />,
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
